import type { ProjectPhase } from "./types";

type DatedPhase = Pick<ProjectPhase, "id" | "status" | "sort_order" | "actual_start_date" | "actual_end_date">;

const bySortOrder = (a: DatedPhase, b: DatedPhase): number => a.sort_order - b.sort_order || a.id.localeCompare(b.id);

// 실제 기간이 그 날을 덮는가 — 끝나지 않은 공정은 시작일 이후 전부.
const coversOn = (p: DatedPhase, date: string): boolean =>
  p.actual_start_date !== null &&
  p.actual_start_date <= date &&
  (p.actual_end_date === null || p.actual_end_date >= date);

// 그날 고를 공정과 기본값 — 오늘은 진행 공정·착수 뒤 보류한 공정과 오늘 끝낸 공정(진행 중이 하나면 기본값), 지난 날은 실제 날짜로 그날 진행이 확실한 공정이 하나일 때만 기본값(#24).
export const photoPhasesOn = <T extends DatedPhase>(
  phases: readonly T[],
  date: string,
  today: string
): { options: T[]; defaultId: string | null } => {
  const active = phases.filter((p) => p.status === "ACTIVE");
  if (date >= today) {
    const held = phases.filter((p) => p.status === "HOLD" && coversOn(p, date));
    const endedToday = phases.filter((p) => p.actual_end_date !== null && coversOn(p, date));
    const options = [...new Set([...active, ...held, ...endedToday])].sort(bySortOrder);
    return { options, defaultId: active.length === 1 ? active[0].id : null };
  }
  const mayBeOn = phases.filter((p) => coversOn(p, date));
  const sure = mayBeOn.filter((p) => p.actual_end_date !== null || p.status === "ACTIVE");
  const options = [...new Set([...mayBeOn, ...active])].sort(bySortOrder);
  const defaultId = sure.length === 1 && mayBeOn.length === 1 ? sure[0].id : null;
  return { options, defaultId };
};

// 사진에 붙일 공정 — 안 골랐으면(undefined) 기본값, 「공정 없음」(null)은 그대로, 고른 공정이 목록에서 빠지면 없음으로 두고 lost.
export const photoPhaseOf = (
  phases: readonly { id: string }[],
  choice: string | null | undefined,
  fallback: string | null
): { id: string | null; lost: boolean } => {
  if (choice === null) return { id: null, lost: false };
  if (choice === undefined) {
    return { id: fallback !== null && phases.some((p) => p.id === fallback) ? fallback : null, lost: false };
  }
  return phases.some((p) => p.id === choice) ? { id: choice, lost: false } : { id: null, lost: true };
};
